import {
    sanitizeSupportDiagnostics,
    type ISupportDiagnostics,
    type ISupportDiagnosticsEnvironment,
    type ISupportDiagnosticsShare,
} from '@srm/screen-sharing/light';
import { APP_VERSION } from '@/configuration/appVersion';
import { ConfigService } from '@/configuration/services/configService';
import { auth } from '@/auth';
import type { RootState } from '@/store';
import { recentSupportConsole } from '@utils/supportErrorLog';
import { recentSupportRequests } from '@utils/supportNetworkLog';
import { supportPerformance } from '@utils/supportPerformance';
import { versionUpdate } from '@utils/versionUpdate';
import { describeBrowser } from './supportUserContext';
import { supportDocumentDiagnostics } from './supportDocument';
import { supportSubstitution } from './supportSubstitution';

/** Что знает о себе показ: транспорт, качество, сколько специалистов смотрят. */
export type SupportShareInfo = ISupportDiagnosticsShare;

interface INavigatorExtras {
    userAgentData?: { platform?: string };
    connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
    deviceMemory?: number;
}

const MB = 1024 * 1024;

/** Группы пользователя приходят строкой «А; Б» или «А, Б». */
const groups = (value: unknown): string[] | undefined => {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value !== 'string') return undefined;
    const list = value
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter(Boolean);
    return list.length ? list : undefined;
};

function environment(): ISupportDiagnosticsEnvironment {
    const extras = navigator as Navigator & INavigatorExtras;
    const heap = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    const connection = extras.connection;
    const zoom = window.visualViewport?.scale;
    const browser = describeBrowser(navigator.userAgent);
    const memory = {
        ...(extras.deviceMemory ? { deviceGb: extras.deviceMemory } : {}),
        ...(heap
            ? {
                  heapUsedMb: Math.round(heap.usedJSHeapSize / MB),
                  heapLimitMb: Math.round(heap.jsHeapSizeLimit / MB),
              }
            : {}),
    };
    return {
        userAgent: navigator.userAgent,
        ...(browser ? { browser } : {}),
        platform: extras.userAgentData?.platform || navigator.platform || undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        utcOffsetMin: -new Date().getTimezoneOffset(),
        locale: navigator.language,
        languages: [...(navigator.languages ?? [])],
        screen: {
            width: window.screen?.width ?? 0,
            height: window.screen?.height ?? 0,
            dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
        },
        window: { width: window.innerWidth, height: window.innerHeight, ...(zoom && zoom !== 1 ? { zoom } : {}) },
        online: navigator.onLine,
        ...(connection
            ? {
                  connection: {
                      effectiveType: connection.effectiveType,
                      downlinkMbps: connection.downlink,
                      rttMs: connection.rtt,
                      saveData: connection.saveData,
                  },
              }
            : {}),
        ...(Object.keys(memory).length ? { memory } : {}),
        ...(navigator.hardwareConcurrency ? { cpuCores: navigator.hardwareConcurrency } : {}),
    };
}

/**
 * Пакет диагностики (§43.2) из того, что вкладка уже знает: окружение браузера, приложение и пользователь (стор),
 * открытый документ, последние запросы и ошибки страницы, долгие задачи, показ. Значений полей, тел запросов, токенов и
 * содержимого хранилищ нет; результат прошёл санитайзер пакета — его же видит пользователь в «Посмотреть, что уйдёт».
 */
export function collectSupportDiagnostics(state: RootState, share?: SupportShareInfo): ISupportDiagnostics {
    const config = ConfigService.get();
    const user = state.auth.user;
    const canView = user?.canView
        ? Object.entries(user.canView)
              .filter(([, allowed]) => allowed === true)
              .map(([key]) => key)
        : undefined;
    const substitution = supportSubstitution();
    const document = supportDocumentDiagnostics();
    const raw = {
        schema: 1,
        collectedAt: new Date().toISOString(),
        environment: environment(),
        app: {
            frontVersion: APP_VERSION,
            serverVersion: state.appData.version ?? undefined,
            updateRequired: versionUpdate.getState().mandatory || undefined,
            environment: config.environment,
            title: config.application.titlePrefix || undefined,
            user: {
                id: user?.id !== undefined ? String(user.id) : '',
                role: user?.role || undefined,
                accessGroups: groups(user?.accessGroups),
                workGroups: groups(user?.workGroups),
                canView,
            },
            ...(substitution ? { substitution } : {}),
            authMode: auth.getMode(),
        },
        ...(document ? { document } : {}),
        network: recentSupportRequests(),
        console: recentSupportConsole(),
        performance: supportPerformance(),
        ...(share ? { share } : {}),
    };
    // Санитайзер пакета режет строки и разделы по пределам и маскирует секреты — то же увидит и сервер.
    return sanitizeSupportDiagnostics(raw)!;
}
